import React from "react";
import { omit } from "radash";
import { Button } from "primereact/button";
import { DialogProps } from "primereact/dialog";
import { ArrayUtils, EventUtils } from "@nubeteck/utils";
import { useForm, useFieldArray } from "react-hook-form";
import { useGlobalQuery, useGlobalMutation } from "@hooks";
import { CardTable, IDataTableColumnsProps } from "@nubeteck/prime";
import { Form, Dialog, Dropdown, InputText, InputSwitch } from "@components";

import { IStrategicPlansTemplates } from "../../interfaces";

export interface ISettingsTemplatesDialogProps extends DialogProps {
  templateId: number;
}

type FormValues = IStrategicPlansTemplates & {
  Detalles?: { TipoDeNivelId: number }[];
};

const model = {
  from: (data: IStrategicPlansTemplates): FormValues => {
    return {
      Nombre: data.Nombre,
      TipoId: data.TipoId,
      EstadoId: data.EstadoId,
      Detalles: data.Detalles ?? [],
      PlantillaId: data.PlantillaId,
      EsPorDefecto: data.EsPorDefecto,
    };
  },
  to: (data: FormValues): IStrategicPlansTemplates => {
    return {
      Nombre: data.Nombre,
      TipoId: data.TipoId,
      EstadoId: data.EstadoId,
      PlantillaId: data.PlantillaId,
      EsPorDefecto: data.EsPorDefecto,
      Detalles: data.Detalles?.map((detail, index) => ({
        Id: detail.Id,
        Orden: index + 1,
        EstadoId: detail.EstadoId ?? 1,
        PlantillaId: data.PlantillaId ?? 0,
        TipoDeNivelId: detail.TipoDeNivelId,
      })),
    };
  },
};

const SettingsTemplatesDialog = ({
  templateId,
  ...props
}: ISettingsTemplatesDialogProps) => {
  const createTemplate = useGlobalMutation("StrategicPlansTemplates", "create");
  const updateTemplate = useGlobalMutation("StrategicPlansTemplates", "update");
  const getCreate = useGlobalQuery("StrategicPlansTemplates", "getCreate");
  const getUpdate = useGlobalQuery(
    "StrategicPlansTemplates",
    "getUpdate",
    templateId,
    { enabled: !!templateId },
  );

  const form = useForm<FormValues>({});
  const { reset } = form;

  const { fields, append, remove } = useFieldArray({
    name: "Detalles",
    control: form.control,
  });

  React.useEffect(() => {
    if (!templateId && getCreate.data) {
      reset(
        omit(getCreate.data, [
          "TipoSelect",
          "Validaciones",
          "DetallesTiposSelect",
          "ValidacionesDetalles",
        ]),
        { keepDefaultValues: false },
      );
    }
  }, [reset, getCreate.data, templateId]);

  React.useEffect(() => {
    if (templateId && getUpdate.data) {
      reset(model.from(getUpdate.data), { keepDefaultValues: true });
    }
  }, [reset, getUpdate.data, templateId]);

  const onSubmit = (values: FormValues) => {
    if (templateId) {
      return updateTemplate.mutateAsync(model.to(values), {
        onSuccess: () => {
          form.reset();
          props.onHide();
        },
      });
    }
    return createTemplate.mutateAsync(model.to(values), {
      onSuccess: () => {
        form.reset();
        props.onHide();
      },
    });
  };

  const detailsTypes = React.useMemo(() => {
    if (templateId) return getUpdate.data?.DetallesTiposSelect ?? [];
    else return getCreate.data?.DetallesTiposSelect ?? [];
  }, [
    getCreate.data?.DetallesTiposSelect,
    getUpdate.data?.DetallesTiposSelect,
    templateId,
  ]);

  const detailsTypesOptions = React.useMemo(() => {
    if (!detailsTypes.length) return [];
    return detailsTypes.map((detailType) => {
      const isSelected = fields.some(
        (field) => field.TipoDeNivelId === detailType.DetalleTipoId,
      );
      return {
        disabled: isSelected,
        label: detailType.Nombre,
        value: detailType.DetalleTipoId,
      };
    });
  }, [detailsTypes, fields]);

  const validationsDetails = React.useMemo(() => {
    if (templateId) return getUpdate.data?.ValidacionesDetalles;
    else return getCreate.data?.ValidacionesDetalles;
  }, [
    getCreate.data?.ValidacionesDetalles,
    getUpdate.data?.ValidacionesDetalles,
    templateId,
  ]);

  const columns: IDataTableColumnsProps<(typeof fields)[0]>[] = [
    {
      header: "#",
      field: "id",
      style: { width: "50px" },
      body: ({ id }) => {
        const index = fields.findIndex((field) => field.id === id);
        return <span className="font-bold">{index + 1}</span>;
      },
    },
    {
      header: "Tipo",
      field: "TipoDeNivelId",
      body: ({ id }) => {
        const index = fields.findIndex((field) => field.id === id);
        return (
          <Dropdown
            label=""
            fieldClassName="m-0"
            className="p-inputtext-sm"
            options={detailsTypesOptions}
            placeholder="Selecciona un tipo"
            name={`Detalles.${index}.TipoDeNivelId`}
            rules={validationsDetails?.TipoDeNivelId}
          />
        );
      },
    },
  ];

  const types = React.useMemo(() => {
    if (templateId) return getUpdate.data?.TipoSelect ?? [];
    else return getCreate.data?.TipoSelect ?? [];
  }, [getCreate.data?.TipoSelect, getUpdate.data?.TipoSelect, templateId]);

  const typesOptions = React.useMemo(() => {
    if (!types.length) return [];
    return ArrayUtils.selectLabelValue(types ?? [], "Nombre", "TipoId");
  }, [types]);

  const validations = React.useMemo(() => {
    if (templateId) return getUpdate.data?.Validaciones;
    else return getCreate.data?.Validaciones;
  }, [getCreate.data?.Validaciones, getUpdate.data?.Validaciones, templateId]);

  const loading = React.useMemo(() => {
    if (templateId) return getUpdate.isPending;
    return getCreate.isPending;
  }, [templateId, getCreate.isPending, getUpdate.isPending]);

  return (
    <Dialog
      {...props}
      loading={loading}
      className="w-full lg:w-8 xl:w-6"
      subtitle="Rellena los datos del formulario"
      title={templateId ? `Editando plantilla` : "Creando nueva plantilla"}
      onHide={() => {
        if (templateId) form.reset();
        props.onHide();
      }}
      footer={(props) => (
        <div className="flex flex-1 justify-content-end gap-2">
          <Button
            size="small"
            label="Cancelar"
            severity="secondary"
            onClick={() => {
              form.reset();
              props.onHide();
            }}
          />
          <Button
            size="small"
            onClick={form.handleSubmit(onSubmit)}
            label={templateId ? "Guardar cambios" : "Crear plantilla"}
            loading={
              loading || createTemplate.isPending || updateTemplate.isPending
            }
          />
        </div>
      )}
    >
      <Form form={form}>
        <div className="flex flex-column md:flex-row md:gap-3">
          <InputText
            name="Nombre"
            label="Nombre"
            fieldClassName="flex-1"
            rules={validations?.["Nombre"]}
            className="p-inputtext-sm flex-1"
            placeholder="Escribe un nombre..."
          />
          <InputSwitch
            name="EsPorDefecto"
            fieldClassName="flex-1"
            label="¿Es por defecto?"
            rules={validations?.["EsPorDefecto"]}
          />
        </div>
        <Dropdown
          name="TipoId"
          options={typesOptions}
          label="Tipo de plantilla"
          className="p-inputtext-sm"
          rules={validations?.["TipoId"]}
          placeholder="Selecciona un tipo"
        />
        <CardTable
          type="data"
          value={fields}
          hideBorderStyles
          hideGlobalSearch
          hideSelectFilter
          columns={columns}
          paginator={false}
          actionMenu="non-float"
          hideFilterSettingsIcon
          title="Orden de los detalles"
          actionColumnProps={{ bodyStyle: { width: "1%" } }}
          tableActions={({ id }) => {
            const index = fields.findIndex((field) => field.id === id);
            return [
              {
                icon: "x",
                label: "Eliminar",
                iconClassName: "text-red-500",
                onClick: EventUtils.callEvent(remove, index),
              },
            ];
          }}
          headActions={[
            <Button
              size="small"
              type="button"
              key="AddDetails"
              className="flex w-auto"
              label="Agregar detalle"
              disabled={detailsTypes.length === fields.length}
              onClick={() =>
                append({ EstadoId: 1 } as never, {
                  shouldFocus: true,
                })
              }
            />,
          ]}
        />
      </Form>
    </Dialog>
  );
};

export default SettingsTemplatesDialog;
